import React, { ReactNode } from 'react';
import styled from 'styled-components';
import SvgChevronRight from '../../icons/ChevronRight';
import Checkbox from '../Selectable/Checkbox';
import { space } from '../../theme/layout';
import { style } from '../../utils/css';
import BaseClickable, {
  BaseClickableProps,
} from '../BaseClickable/BaseClickable';
import Box from '../Box/Box';
import Stack from '../Stack/Stack';
import Text from '../Text/Text';

const Avatar = styled.img(
  style({
    width: space[6],
    height: space[6],
    borderRadius: 'full',
    display: 'block',
    flex: 0,
  }),
);

export type ChoiceCardProps = {
  /** The title text or node */
  title: ReactNode;

  /** The subtitel text or node */
  subtitle?: ReactNode;

  /** An image url to display in the icon slot (overrides `icon` prop) */
  iconUrl?: string;

  /** A react node to display as the icon */
  icon?: ReactNode;

  /** Display as a table row, with no padding or rounding */
  row?: boolean;

  /** Display the component with a checkbox instead of a right chevron */
  checkbox?: boolean;

  /** Display the component as selected. If it is a checkbox card it toggles the checkbox to a 'selected' state */
  selected?: boolean;
} & Omit<
  BaseClickableProps,
  | 'startIcon'
  | 'endIcon'
  | 'loading'
  | 'inline'
  | 'accessory'
  | 'children'
  | 'title'
>;

const StyledChoiceCard = styled(BaseClickable)<Partial<ChoiceCardProps>>(
  ({ row, selected, checkbox }) =>
    style({
      ...(row
        ? {
            borderBottom: 'solid 1px',
            borderRadius: 'none',
          }
        : { border: 'solid 1px', borderRadius: 'base' }),

      '--focus-offset': '0',
      backgroundColor: selected && checkbox ? 'gray-200' : 'white',
      ...(selected
        ? {
            borderColor: 'gray-600',
          }
        : {
            borderColor: 'gray-200',
          }),
      cursor: 'pointer',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 2,
      ':disabled': {
        pointerEvents: 'none',
        opacity: 0.5,
      },
      ':hover': {
        borderColor: selected ? 'gray-600' : 'gray-300',
      },
      ':focus': {
        '--focus-width': '1px',
        boxShadow: 'focus',
        borderColor: 'gray-600',
      },
      ':active': {
        borderColor: 'gray-500',
      },
    }),
);

const getRightIcon = (checkbox?: boolean, selected?: boolean) => {
  if (checkbox) {
    return <Checkbox checked={!!selected} />;
  }
  return <SvgChevronRight />;
};

/**
 * A full width, low-visual-weight button with a disclosure indicator used in multi-step forms.
 *
 */
const ChoiceCard = ({
  title,
  subtitle,
  icon,
  iconUrl,
  checkbox,
  selected,
  ...rest
}: ChoiceCardProps) => (
  <StyledChoiceCard
    checkbox={checkbox}
    contentProps={{ flex: 1 }}
    selected={selected}
    {...rest}
  >
    <Stack
      row
      width="100%"
      flex="1"
      justifyContent="space-between"
      alignItems="center"
    >
      <Stack alignItems="center" row space={2} textColor="gray-500">
        {(() => {
          if (iconUrl)
            return (
              <Avatar
                width={space[6]}
                height={space[6]}
                src={iconUrl}
                alt="Icon"
              />
            );
          if (icon) return icon;
          return null;
        })()}
        <Stack alignItems="flex-start">
          {typeof title === 'string' ? (
            <Text variant="subtitle">{title}</Text>
          ) : (
            title
          )}
          {subtitle &&
            (typeof subtitle === 'string' ? (
              <Text muted>{subtitle}</Text>
            ) : (
              subtitle
            ))}
        </Stack>
      </Stack>

      <Box>{getRightIcon(checkbox, selected)}</Box>
    </Stack>
  </StyledChoiceCard>
);

ChoiceCard.displayName = 'ChoiceCard';
export default ChoiceCard;
